import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { MembershipBanner } from '../components/MembershipModal';
import { useSEO } from '../hooks/useSEO';
import api, { likePost } from '../lib/api';
import { PostCard } from './DashboardPage';
import { 
  ExternalLink, 
  Pin, 
  RefreshCw, 
  Newspaper,
  ChevronDown,
  Globe
} from 'lucide-react';

// API helpers
const getEWTNPosts = (params) => api.get('/ewtn/posts', { params });
const triggerEWTNFetch = () => api.post('/ewtn/fetch');
const pinEWTNPost = (postId) => api.post(`/ewtn/pin/${postId}`);

export default function EWTNNewsPage({ user, isPublic = false }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 10;

  useSEO({
    title: "EWTN News",
    description: "Stay updated with Catholic news from EWTN. Read the latest articles on faith, Church life, and world events from the Eternal Word Television Network.",
    path: "/ewtn-news"
  });

  const isAdmin = user?.role === 'admin' || user?.is_site_admin;
  const isParishAdmin = user?.role === 'parish_admin' || user?.is_parish_admin || isAdmin;

  const loadPosts = async (loadMore = false) => {
    try {
      const currentSkip = loadMore ? skip : 0;
      const res = await getEWTNPosts({ limit, skip: currentSkip });
      if (loadMore) {
        setPosts(prev => [...prev, ...res.data.posts]);
      } else {
        setPosts(res.data.posts);
      }
      setHasMore(res.data.has_more);
      setSkip(currentSkip + limit);
    } catch (err) {
      toast.error('Failed to load EWTN News');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualFetch = async () => {
    setFetchLoading(true);
    try {
      const res = await triggerEWTNFetch();
      toast.success(`Fetched ${res.data.created} new posts`);
      loadPosts();
    } catch (err) {
      toast.error('Failed to fetch EWTN News');
    } finally {
      setFetchLoading(false);
    }
  };

  const handlePinPost = async (postId) => {
    try {
      const res = await pinEWTNPost(postId);
      toast.success(res.data.message);
      setPosts(prev => prev.map(p => 
        p.post_id === postId 
          ? { ...p, is_pinned: res.data.pinned }
          : p
      ));
    } catch (err) {
      toast.error('Failed to pin post');
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await likePost(postId);
      setPosts(prev => prev.map(p => p.post_id === postId ? {
        ...p,
        user_liked: res.data.liked,
        like_count: res.data.like_count
      } : p));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="ewtn-news-page">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B3A5C] to-[#2C5F8A] text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                <span className="text-2xl font-bold font-['Playfair_Display'] text-[#D4AF37]">EWTN</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold font-['Playfair_Display']">EWTN News</h1>
                <p className="text-white/80 text-sm font-['Nunito']">Eternal Word Television Network</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualFetch}
                  disabled={fetchLoading}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="ewtn-fetch-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${fetchLoading ? 'animate-spin' : ''}`} />
                  Fetch Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {isPublic && (
          <MembershipBanner context="ewtn" className="mb-6" />
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* News Posts */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold font-['Playfair_Display'] mb-4 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-[#1B3A5C]" />
              Recent EWTN News
            </h2>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="bg-white border-[#E5E0D5]">
                      <CardContent className="p-4">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-full mb-1" />
                        <Skeleton className="h-3 w-2/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <Card className="bg-white border-[#E5E0D5]">
                  <CardContent className="p-8 text-center">
                    <Globe className="w-12 h-12 mx-auto text-[#1B3A5C]/30 mb-4" />
                    <p className="text-[#6B5344]/60 font-['Nunito']">No EWTN News posts yet.</p>
                    {isAdmin && (
                      <Button 
                        className="mt-4 bg-[#1B3A5C] hover:bg-[#2C5F8A]"
                        onClick={handleManualFetch}
                      >
                        Fetch Now
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {posts.map(post => (
                    <PostCard
                      key={post.post_id}
                      post={post}
                      currentUserId={user?.user_id}
                      onLike={handleLike}
                      onComment={() => {}}
                    />
                  ))}

                  {hasMore && (
                    <Button
                      variant="outline"
                      className="w-full border-[#E5E0D5] text-[#1B3A5C]"
                      onClick={() => loadPosts(true)}
                    >
                      <ChevronDown className="w-4 h-4 mr-2" />
                      Load More
                    </Button>
                  )}
                </div>
              )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Playfair_Display']">About EWTN</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#6B5344]/80 font-['Nunito'] leading-relaxed">
                  EWTN Global Catholic Network is the largest religious media network in the world. 
                  Articles are automatically fetched from the EWTN News feed every hour, bringing you the 
                  latest on Catholic news, faith, and Church life around the world.
                </p>
                <div className="mt-4 p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
                  <p className="text-xs text-[#6B5344]/60 font-['Nunito'] italic">
                    "We are not combatants, we are fishers. We catch them and let the Holy Spirit 
                    clean them."
                  </p>
                  <p className="text-xs text-[#1B3A5C] mt-2 font-semibold">— Mother Angelica, EWTN Founder</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Playfair_Display']">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <a 
                  href="https://www.ewtnnews.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#1B3A5C] hover:text-[#2C5F8A] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  EWTN News
                </a>
                <a 
                  href="https://www.ewtn.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#1B3A5C] hover:text-[#2C5F8A] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  EWTN Official Site
                </a>
                <a 
                  href="https://www.ncregister.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#1B3A5C] hover:text-[#2C5F8A] font-['Nunito']"
                >
                  <ExternalLink className="w-4 h-4" />
                  National Catholic Register
                </a>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-[#1B3A5C]/10 to-[#D4AF37]/10 border-[#E5E0D5]">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Newspaper className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C1810] font-['Playfair_Display'] text-sm">
                      Stay Informed
                    </h3>
                    <p className="text-xs text-[#6B5344]/70 font-['Nunito'] mt-1">
                      EWTN News delivers Catholic journalism with a global perspective. 
                      New articles are fetched automatically every hour.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
